import React, { useEffect, useRef, useState } from 'react';
import { LessonResource, PresentationSlide } from '../../types/v2';
import { googleDriveApi } from '../../services/api';
import { lessonPlansApi } from '../../services/apiV2';
import { pickGoogleDriveFile } from '../../utils/googleDrivePicker';
import { googleDriveFileIdFromUrl, googleDriveResourceType, googleDriveResourceUrl } from '../../utils/lessonResources';

const emptyResource = (): LessonResource => ({
  title: '',
  src: '',
  resource_type: 'document',
  audience: 'student',
  needs_printing: false,
  description: '',
  availability: 'lesson',
  available_during_apply: true,
});

const GOOGLE_SLIDES_MIME = 'application/vnd.google-apps.presentation';
const POWERPOINT_MIMES = new Set([
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

const isPresentationResource = (resource: LessonResource) => (
  resource.source_mime_type === GOOGLE_SLIDES_MIME
  || POWERPOINT_MIMES.has(resource.source_mime_type || '')
  || /\.(ppt|pptx)(?:$|[?#])/i.test(resource.src)
  || /docs\.google\.com\/presentation\//i.test(resource.src)
);

const PresentationSlideThumbnail: React.FC<{ slide: PresentationSlide }> = ({ slide }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '80px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={containerRef} className="flex aspect-video w-full items-center justify-center rounded border border-slate-200 bg-slate-50">
      {visible && !failed ? (
        <img
          src={slide.thumbnail_url}
          alt={`Slide ${slide.slide_number} preview`}
          className="h-full w-full rounded object-contain"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : failed ? (
        <button type="button" className="px-2 text-xs font-medium text-violet-800" onClick={() => setFailed(false)}>
          Retry preview
        </button>
      ) : (
        <span className="text-xs text-slate-500">Preview loads when visible</span>
      )}
    </div>
  );
};

export const lessonMaterialIssue = (resource: LessonResource, stepOrders: number[]): string | null => {
  if (!resource.title.trim()) return 'Add a title.';
  if (!resource.src.trim().startsWith('https://')) return 'Use a secure URL beginning with https://.';
  if (resource.resource_type === 'image' && !resource.alt?.trim()) return 'Add an image description for accessibility.';
  if (resource.availability === 'step' && !stepOrders.includes(Number(resource.step_order))) {
    return 'Choose a valid lesson step.';
  }
  if (resource.presentation_mode === 'selected_slides' && !resource.selected_slides?.length) {
    return 'Choose at least one slide, or use the entire deck.';
  }
  return null;
};

const audienceWarning = (resource: LessonResource): string | null => {
  if (resource.access_scope === 'restricted' && resource.audience !== 'teacher') {
    return 'Student access warning: this Drive file is restricted, but its audience includes students. They may be unable to open it. Change Drive sharing or make this material teacher-only.';
  }
  if (resource.audience === 'teacher') return null;
  return /(answer\s*key|teacher\s*guide|solutions?|setup\s*instructions?)/i.test(resource.title)
    ? 'This title sounds teacher-facing, but students can access it. Confirm the audience.'
    : null;
};

export const LessonMaterialsEditor: React.FC<{
  lessonPlanId?: number;
  resources: LessonResource[];
  steps: Array<{ order: number; title: string }>;
  onChange: (resources: LessonResource[]) => void;
}> = ({ lessonPlanId, resources, steps, onChange }) => {
  const stepOrders = steps.map((step) => Number(step.order));
  const [driveBusyIndex, setDriveBusyIndex] = useState<number | null>(null);
  const [driveError, setDriveError] = useState('');
  const [discoveryBusy, setDiscoveryBusy] = useState(false);
  const [discoveryMessage, setDiscoveryMessage] = useState('');
  const [discoveredResources, setDiscoveredResources] = useState<Array<LessonResource & { drive_path?: string; match_reason?: string }>>([]);
  const [slideCatalogs, setSlideCatalogs] = useState<Record<string, PresentationSlide[]>>({});
  const update = (index: number, changes: Partial<LessonResource>) => {
    onChange(resources.map((resource, resourceIndex) => (
      resourceIndex === index ? { ...resource, ...changes } : resource
    )));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= resources.length) return;
    const next = [...resources];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const checkDriveAccess = async (index: number, fileId: string) => {
    setDriveBusyIndex(index);
    setDriveError('');
    try {
      const response = await googleDriveApi.fileAccess(fileId);
      update(index, {
        source_file_id: fileId,
        source_name: response.data?.name || resources[index]?.source_name,
        source_mime_type: response.data?.mime_type || resources[index]?.source_mime_type,
        preview_url: response.data?.preview_url,
        access_scope: response.data?.access_scope || 'unknown',
        access_checked_at: response.data?.access_checked_at,
      });
    } catch (error: any) {
      setDriveError(error?.response?.data?.error || error?.message || 'Unable to check Google Drive access.');
    } finally {
      setDriveBusyIndex(null);
    }
  };

  const chooseFromDrive = async (index: number) => {
    setDriveBusyIndex(index);
    setDriveError('');
    try {
      const file = await pickGoogleDriveFile();
      if (!file) return;
      const nextResource = {
        ...resources[index],
        title: resources[index].title.trim() || file.name,
        src: googleDriveResourceUrl(file.id, file.mimeType, file.url),
        resource_type: googleDriveResourceType(file.mimeType),
        source_file_id: file.id,
        source_name: file.name,
        source_mime_type: file.mimeType,
        access_scope: 'unknown' as const,
        presentation_mode: file.mimeType === GOOGLE_SLIDES_MIME || POWERPOINT_MIMES.has(file.mimeType)
          ? 'entire_deck' as const
          : undefined,
        selected_slides: undefined,
        presentation_synced_at: undefined,
      };
      onChange(resources.map((resource, resourceIndex) => resourceIndex === index ? nextResource : resource));
      const response = await googleDriveApi.fileAccess(file.id);
      onChange(resources.map((resource, resourceIndex) => resourceIndex === index ? {
        ...nextResource,
        source_name: response.data?.name || file.name,
        source_mime_type: response.data?.mime_type || file.mimeType,
        preview_url: response.data?.preview_url,
        access_scope: response.data?.access_scope || 'unknown',
        access_checked_at: response.data?.access_checked_at,
      } : resource));
    } catch (error: any) {
      setDriveError(error?.response?.data?.error || error?.message || 'Unable to choose a Google Drive file.');
    } finally {
      setDriveBusyIndex(null);
    }
  };

  const loadPresentationSlides = async (index: number, requestedMode?: LessonResource['presentation_mode']) => {
    const resource = resources[index];
    const fileId = resource.source_file_id || googleDriveFileIdFromUrl(resource.src);
    if (!fileId || resource.source_mime_type !== GOOGLE_SLIDES_MIME) return;
    setDriveBusyIndex(index);
    setDriveError('');
    try {
      const response = await googleDriveApi.presentationSlides(fileId);
      const slides = Array.isArray(response.data?.slides) ? response.data.slides as PresentationSlide[] : [];
      setSlideCatalogs((current) => ({ ...current, [fileId]: slides }));
      const refreshedById = new Map(slides.map((slide) => [slide.object_id, slide]));
      const refreshedSelection = (resource.selected_slides || [])
        .map((slide) => refreshedById.get(slide.object_id))
        .filter(Boolean) as PresentationSlide[];
      update(index, {
        presentation_mode: requestedMode || resource.presentation_mode || 'entire_deck',
        selected_slides: refreshedSelection,
        presentation_synced_at: response.data?.synced_at,
      });
    } catch (error: any) {
      setDriveError(error?.response?.data?.error || error?.message || 'Unable to load slides from this presentation.');
    } finally {
      setDriveBusyIndex(null);
    }
  };

  const togglePresentationSlide = (index: number, slide: PresentationSlide) => {
    const selected = resources[index].selected_slides || [];
    const alreadySelected = selected.some((item) => item.object_id === slide.object_id);
    update(index, {
      presentation_mode: 'selected_slides',
      selected_slides: alreadySelected
        ? selected.filter((item) => item.object_id !== slide.object_id)
        : [...selected, slide],
    });
  };

  const movePresentationSlide = (index: number, slideIndex: number, direction: -1 | 1) => {
    const selected = [...(resources[index].selected_slides || [])];
    const target = slideIndex + direction;
    if (target < 0 || target >= selected.length) return;
    [selected[slideIndex], selected[target]] = [selected[target], selected[slideIndex]];
    update(index, { selected_slides: selected });
  };

  const discoverFromDriveFolder = async () => {
    if (!lessonPlanId) return;
    setDiscoveryBusy(true);
    setDriveError('');
    setDiscoveryMessage('');
    setDiscoveredResources([]);
    try {
      const response = await lessonPlansApi.discoverDriveMaterials(lessonPlanId);
      const candidates = Array.isArray(response.data?.resources) ? response.data.resources : [];
      setDiscoveredResources(candidates);
      setDiscoveryMessage(String(response.data?.message || ''));
    } catch (error: any) {
      setDriveError(error?.response?.data?.detail || error?.response?.data?.error || 'Unable to search the shared Drive materials folder.');
    } finally {
      setDiscoveryBusy(false);
    }
  };

  const useDiscoveredResources = () => {
    onChange([...resources, ...discoveredResources]);
    setDiscoveryMessage(`${discoveredResources.length} Drive material${discoveredResources.length === 1 ? '' : 's'} added. Save the lesson to keep them.`);
    setDiscoveredResources([]);
  };

  return (
    <section className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-indigo-950">Lesson Materials</h2>
          <p className="mt-1 text-xs text-indigo-800">Links, handouts, guides, and other lesson-level materials.</p>
        </div>
        <button
          type="button"
          className="rounded border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-50"
          onClick={() => onChange([...resources, emptyResource()])}
        >
          Add material
        </button>
      </div>

      {resources.length === 0 && lessonPlanId ? (
        <div className="mt-3 rounded-lg border border-dashed border-indigo-200 bg-white/70 p-3">
          <p className="text-xs font-semibold text-indigo-950">No bundle materials attached</p>
          <p className="mt-1 text-xs text-slate-600">Search the configured curriculum Drive folder for a deterministic meeting/title match.</p>
          <button
            type="button"
            className="mt-2 rounded border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
            disabled={discoveryBusy}
            onClick={() => void discoverFromDriveFolder()}
          >
            {discoveryBusy ? 'Searching Drive…' : 'Find matching Drive materials'}
          </button>
        </div>
      ) : null}

      {discoveryMessage ? <p className="mt-3 rounded bg-blue-50 px-3 py-2 text-xs text-blue-800">{discoveryMessage}</p> : null}
      {discoveredResources.length > 0 ? (
        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
          <p className="text-xs font-semibold text-blue-950">Review folder matches before adding</p>
          <div className="mt-2 space-y-2">
            {discoveredResources.map((resource) => (
              <div key={resource.source_file_id || resource.src} className="rounded border border-blue-100 bg-white px-2.5 py-2">
                <p className="text-xs font-semibold text-slate-900">{resource.title}</p>
                {resource.drive_path ? <p className="mt-1 break-words text-[11px] text-slate-500">{resource.drive_path}</p> : null}
                <p className="mt-1 text-[11px] text-slate-600">
                  {resource.audience === 'teacher' ? 'Teacher only' : resource.audience === 'both' ? 'Teacher + students' : 'Students'}
                  {resource.needs_printing ? ' · Needs printing' : ' · Digital'}
                  {resource.match_reason ? ` · ${resource.match_reason}` : ''}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" className="rounded bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700" onClick={useDiscoveredResources}>
              Add {discoveredResources.length} material{discoveredResources.length === 1 ? '' : 's'}
            </button>
            <button type="button" className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700" onClick={() => { setDiscoveredResources([]); setDiscoveryMessage(''); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {resources.length === 0 ? (
        <p className="mt-3 rounded border border-dashed border-indigo-200 bg-white/70 px-3 py-3 text-xs text-slate-600">
          No materials have been added to this lesson.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {driveError ? <p role="alert" className="rounded bg-rose-50 px-3 py-2 text-xs text-rose-700">{driveError}</p> : null}
          {resources.map((resource, index) => (
            <div key={index} className="rounded-lg border border-indigo-100 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Material {index + 1}</p>
                <div className="flex gap-1">
                  <button type="button" className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40" disabled={index === 0} onClick={() => move(index, -1)}>Up</button>
                  <button type="button" className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40" disabled={index === resources.length - 1} onClick={() => move(index, 1)}>Down</button>
                  <button type="button" className="rounded px-2 py-1 text-xs text-rose-700 hover:bg-rose-50" onClick={() => onChange(resources.filter((_, resourceIndex) => resourceIndex !== index))}>Remove</button>
                </div>
              </div>
              <div className="mt-2 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="rounded border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                    disabled={driveBusyIndex !== null}
                    onClick={() => void chooseFromDrive(index)}
                  >
                    {driveBusyIndex === index ? 'Checking Drive...' : 'Choose from Google Drive'}
                  </button>
                  {resource.source_file_id ? (
                    <button
                      type="button"
                      className="rounded border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      disabled={driveBusyIndex !== null}
                      onClick={() => void checkDriveAccess(index, resource.source_file_id!)}
                    >
                      Recheck access
                    </button>
                  ) : googleDriveFileIdFromUrl(resource.src) ? (
                    <button
                      type="button"
                      className="rounded border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      disabled={driveBusyIndex !== null}
                      onClick={() => void checkDriveAccess(index, googleDriveFileIdFromUrl(resource.src)!)}
                    >
                      Check Google Drive access
                    </button>
                  ) : null}
                  {resource.access_scope ? (
                    <span className={`rounded-full px-2 py-1 text-xs ${
                      resource.access_scope === 'anyone' || resource.access_scope === 'organization'
                        ? 'bg-emerald-50 text-emerald-800'
                        : resource.access_scope === 'restricted'
                          ? 'bg-amber-50 text-amber-800'
                          : 'bg-slate-100 text-slate-700'
                    }`}>
                      {resource.access_scope === 'anyone'
                        ? 'Anyone with link can open'
                        : resource.access_scope === 'organization'
                          ? 'Organization access'
                          : resource.access_scope === 'restricted'
                            ? 'Restricted access'
                            : 'Access not verified'}
                    </span>
                  ) : null}
                </div>
                <input
                  aria-label={`Material ${index + 1} title`}
                  className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  value={resource.title}
                  onChange={(event) => update(index, { title: event.target.value })}
                  placeholder="Student lab worksheet"
                />
                <input
                  aria-label={`Material ${index + 1} URL`}
                  type="url"
                  className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  value={resource.src}
                  onChange={(event) => update(index, {
                    src: event.target.value,
                    source_file_id: undefined,
                    source_name: undefined,
                    source_mime_type: undefined,
                    preview_url: undefined,
                    access_scope: undefined,
                    access_checked_at: undefined,
                  })}
                  placeholder="https://..."
                />
                <textarea
                  aria-label={`Material ${index + 1} description`}
                  className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                  rows={2}
                  value={resource.description || ''}
                  onChange={(event) => update(index, { description: event.target.value })}
                  placeholder="Optional preparation or usage note"
                />
                <div className="grid grid-cols-2 gap-2">
                  <select
                    aria-label={`Material ${index + 1} type`}
                    className="rounded border border-slate-200 px-2 py-2 text-sm"
                    value={resource.resource_type}
                    onChange={(event) => update(index, { resource_type: event.target.value as LessonResource['resource_type'] })}
                  >
                    <option value="document">Document / PDF</option>
                    <option value="link">External link</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                  <select
                    aria-label={`Material ${index + 1} audience`}
                    className="rounded border border-slate-200 px-2 py-2 text-sm"
                    value={resource.audience || 'student'}
                    onChange={(event) => update(index, { audience: event.target.value as LessonResource['audience'] })}
                  >
                    <option value="student">Students</option>
                    <option value="teacher">Teacher only</option>
                    <option value="both">Teacher and students</option>
                  </select>
                </div>
                {isPresentationResource(resource) ? (
                  <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-violet-900">Presentation display</p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        className={`rounded border px-2.5 py-2 text-xs font-semibold ${resource.presentation_mode !== 'selected_slides' ? 'border-violet-600 bg-violet-600 text-white' : 'border-violet-200 bg-white text-violet-900'}`}
                        onClick={() => update(index, { presentation_mode: 'entire_deck' })}
                      >
                        Entire deck
                      </button>
                      <button
                        type="button"
                        disabled={resource.source_mime_type !== GOOGLE_SLIDES_MIME}
                        className={`rounded border px-2.5 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${resource.presentation_mode === 'selected_slides' ? 'border-violet-600 bg-violet-600 text-white' : 'border-violet-200 bg-white text-violet-900'}`}
                        onClick={() => {
                          update(index, { presentation_mode: 'selected_slides' });
                          void loadPresentationSlides(index, 'selected_slides');
                        }}
                      >
                        Selected slides
                      </button>
                    </div>
                    {resource.source_mime_type !== GOOGLE_SLIDES_MIME ? (
                      <p className="mt-2 text-xs text-violet-800">PowerPoint decks can be shown in full. Convert the file to Google Slides in Drive to choose individual slides.</p>
                    ) : (
                      <button
                        type="button"
                        className="mt-2 rounded border border-violet-200 bg-white px-2.5 py-1.5 text-xs font-medium text-violet-900 disabled:opacity-50"
                        disabled={driveBusyIndex !== null}
                        onClick={() => void loadPresentationSlides(index)}
                      >
                        {driveBusyIndex === index ? 'Loading slides…' : resource.presentation_synced_at ? 'Refresh slides from source' : 'Load slide thumbnails'}
                      </button>
                    )}
                    {resource.source_file_id && slideCatalogs[resource.source_file_id]?.length ? (
                      <div className="mt-3 grid max-h-72 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                        {slideCatalogs[resource.source_file_id].map((slide) => {
                          const checked = Boolean(resource.selected_slides?.some((item) => item.object_id === slide.object_id));
                          return (
                            <label key={slide.object_id} className={`cursor-pointer rounded border p-1.5 ${checked ? 'border-violet-500 bg-violet-100' : 'border-violet-100 bg-white'}`}>
                              <PresentationSlideThumbnail slide={slide} />
                              <span className="mt-1 flex items-center gap-1 text-xs text-slate-800">
                                <input type="checkbox" checked={checked} onChange={() => togglePresentationSlide(index, slide)} />
                                Slide {slide.slide_number}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                    {resource.presentation_mode === 'selected_slides' && resource.selected_slides?.length ? (
                      <div className="mt-3 space-y-1">
                        <p className="text-xs font-semibold text-violet-900">Runner order</p>
                        {resource.selected_slides.map((slide, slideIndex) => (
                          <div key={slide.object_id} className="flex items-center justify-between rounded bg-white px-2 py-1 text-xs text-slate-800">
                            <span>{slideIndex + 1}. Original slide {slide.slide_number}</span>
                            <span className="flex gap-1">
                              <button type="button" disabled={slideIndex === 0} className="rounded px-1.5 py-0.5 hover:bg-slate-100 disabled:opacity-30" onClick={() => movePresentationSlide(index, slideIndex, -1)}>Up</button>
                              <button type="button" disabled={slideIndex === resource.selected_slides!.length - 1} className="rounded px-1.5 py-0.5 hover:bg-slate-100 disabled:opacity-30" onClick={() => movePresentationSlide(index, slideIndex, 1)}>Down</button>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-2">
                  <select
                    aria-label={`Material ${index + 1} availability`}
                    className="rounded border border-slate-200 px-2 py-2 text-sm"
                    value={resource.availability || 'lesson'}
                    onChange={(event) => {
                      const availability = event.target.value as LessonResource['availability'];
                      update(index, {
                        availability,
                        step_order: availability === 'step' ? resource.step_order : undefined,
                      });
                    }}
                  >
                    <option value="lesson">Whole lesson</option>
                    <option value="step">Specific step</option>
                    <option value="unit">Whole unit / hook</option>
                  </select>
                  {resource.availability === 'step' ? (
                    <select
                      aria-label={`Material ${index + 1} step`}
                      className="rounded border border-slate-200 px-2 py-2 text-sm"
                      value={resource.step_order ?? ''}
                      onChange={(event) => update(index, { step_order: Number(event.target.value) })}
                    >
                      <option value="">Choose step</option>
                      {steps.map((step) => (
                        <option key={step.order} value={step.order}>Step {step.order}: {step.title || 'Untitled'}</option>
                      ))}
                    </select>
                  ) : <div />}
                </div>
                {resource.resource_type === 'image' ? (
                  <input
                    aria-label={`Material ${index + 1} image description`}
                    className="w-full rounded border border-slate-200 px-3 py-2 text-sm"
                    value={resource.alt || ''}
                    onChange={(event) => update(index, { alt: event.target.value })}
                    placeholder="Required image description for accessibility"
                  />
                ) : null}
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(resource.needs_printing)}
                    onChange={(event) => update(index, { needs_printing: event.target.checked })}
                  />
                  Needs printing before class
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={resource.available_during_apply !== false}
                    onChange={(event) => update(index, { available_during_apply: event.target.checked })}
                  />
                  Available during APPLY steps
                </label>
                {lessonMaterialIssue(resource, stepOrders) ? (
                  <p role="alert" className="rounded bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
                    {lessonMaterialIssue(resource, stepOrders)}
                  </p>
                ) : null}
                {audienceWarning(resource) ? (
                  <p className={`rounded px-2 py-1.5 text-xs ${resource.access_scope === 'restricted' && resource.audience !== 'teacher' ? 'border border-rose-300 bg-rose-50 font-medium text-rose-800' : 'bg-amber-50 text-amber-800'}`}>
                    {audienceWarning(resource)}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
